import { type Query, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { isAnalyticsTimeout } from '../utils/snapshot-read-retry';

/** A query for this experiment whose last attempt ended in a gateway/client timeout. */
function isTimedOutQueryFor(experimentId: string) {
  return (query: Query): boolean =>
    query.state.status === 'error' &&
    isAnalyticsTimeout(query.state.error) &&
    query.queryKey.includes(experimentId);
}

/**
 * Tracks the experiment page's queries that timed out. Analytics reads no
 * longer auto-retry timeouts (see `retryExperimentSnapshotRead`), so the page
 * surfaces them once and lets the user re-run just those queries on demand.
 */
export function useAnalyticsTimeouts(experimentId: string) {
  const queryClient = useQueryClient();
  const [state, setState] = useState({ hasTimeouts: false, isRetrying: false });

  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const predicate = isTimedOutQueryFor(experimentId);
    // Recomputed on every cache event; bail out when nothing changed so the page doesn't re-render.
    const sync = () => {
      // Active only: an unmounted (e.g. prefetched) timeout re-fetches by itself when its panel mounts.
      const timedOut = cache.findAll({ type: 'active', predicate });
      const next = {
        hasTimeouts: timedOut.length > 0,
        isRetrying: timedOut.some((q) => q.state.fetchStatus === 'fetching'),
      };
      setState((prev) =>
        prev.hasTimeouts === next.hasTimeouts && prev.isRetrying === next.isRetrying ? prev : next,
      );
    };
    sync();
    return cache.subscribe(sync);
  }, [queryClient, experimentId]);

  const retry = useCallback(() => {
    // Only mounted panels; a timed-out prefetch is re-tried when its panel opens.
    void queryClient.refetchQueries({
      type: 'active',
      predicate: isTimedOutQueryFor(experimentId),
    });
  }, [queryClient, experimentId]);

  return { ...state, retry };
}
